import { type ComboboxItemState, Combobox as ComboboxPrimitive } from '@base-ui/react';
import { CheckIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { mergeStyling } from '../../common-functions';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../input-group/input-group';

const Combobox = ComboboxPrimitive.Root;

const ComboboxValue = ({ ...props }: ComboboxPrimitive.Value.Props) => {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
};

const ComboboxTrigger = ({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) => {
  const finalClassName = mergeStyling('shrink ml-1 px-1 border-l border-gray-4 focus:outline-hidden', className);
  return (
    <ComboboxPrimitive.Trigger data-slot="combobox-trigger" className={finalClassName} {...props}>
      {children}

      <OdinIcon
        size={OdinIconSize.Small}
        icon="ChevronDown"
        className="[[data-slot=input-group]:has([data-popup-open])_&]:hidden"
      />

      <OdinIcon
        size={OdinIconSize.Small}
        icon="ChevronUp"
        className="hidden [[data-slot=input-group]:has([data-popup-open])_&]:block"
      />
    </ComboboxPrimitive.Trigger>
  );
};

const ComboboxClear = ({ className, ...props }: ComboboxPrimitive.Clear.Props) => {
  const finalClassName = mergeStyling('shrink ml-1 cursor-pointer px-2 text-gray-3', className);
  return (
    <ComboboxPrimitive.Clear data-slot="combobox-clear" className={finalClassName} {...props}>
      <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} />
    </ComboboxPrimitive.Clear>
  );
};

const ComboboxInput = ({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  error = false,
  inputClassName,
  ...props
}: ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean;
  showClear?: boolean;
  error?: boolean;
  inputClassName?: string;
}) => {
  const finalStyles = mergeStyling(
    'relative py-1 outline-hidden flex-1 items-center justify-center w-full focus:border-primary-1 focus:ring-1 focus:ring-primary-1',
    className,
    { 'border border-error': error }
  );

  return (
    <InputGroup className={finalStyles}>
      <ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} className={inputClassName} />} {...props} />
      <InputGroupAddon
        align={null}
        className="box-border absolute flex items-center justify-center h-full right-0 rounded-sm border-none "
      >
        {showClear && <ComboboxClear />}
        {showTrigger && <ComboboxTrigger />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
};

const ComboboxContent = ({
  className,
  side = 'bottom',
  sideOffset = 3,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>) => {
  const popupStyles = mergeStyling(
    'bg-mono-1 text-gray-1 ring-gray-4 overflow-hidden',
    'shadow-md ring-1 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:shadow-none group/combobox-content max-h-[var(--available-height)]',
    'w-[var(--anchor-width)] origin-[var(--transform-origin)] data-[chips=true]:min-w-[var(--anchor-width)]',
    className
  );
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={popupStyles}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};

const ComboboxList = ({ className, ...props }: ComboboxPrimitive.List.Props) => {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={mergeStyling(
        'no-scrollbar max-h-[min(18rem,calc(var(--available-height)-2.25rem))] scroll-py-1 p-1 data-empty:p-0 overflow-y-auto overscroll-contain',
        className
      )}
      {...props}
    />
  );
};

const ComboboxItem = ({
  className,
  children,
  showIndicator = true,
  ...props
}: ComboboxPrimitive.Item.Props & { showIndicator?: boolean }) => {
  const appliedStyles = (state: ComboboxItemState) => (typeof className === 'function' ? className(state) : className);
  const finalClassName = (state: ComboboxItemState) =>
    mergeStyling(
      'data-[selected]:bg-gray-2 data-[selected]:text-mono-1',
      "data-[highlighted]:bg-primary-5 data-[highlighted]:text-primary-2 not-data-[variant=destructive]:data-[highlighted]:**:text-primary-2 gap-2 py-1 pr-8 pl-1.5 text-sm [&_svg:not([class*='size-'])]:size-4 relative flex w-full cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
      appliedStyles(state)
    );

  return (
    <ComboboxPrimitive.Item data-slot="combobox-item" className={finalClassName} {...props}>
      {children}
      {showIndicator && (
        <ComboboxPrimitive.ItemIndicator
          render={
            <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
              <CheckIcon className="pointer-events-none" />
            </span>
          }
        />
      )}
    </ComboboxPrimitive.Item>
  );
};

const ComboboxGroup = ({ className, ...props }: ComboboxPrimitive.Group.Props) => {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={mergeStyling(className)} {...props} />;
};

const ComboboxLabel = ({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) => {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={mergeStyling('text-gray-2 px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
};

const ComboboxCollection = ({ ...props }: ComboboxPrimitive.Collection.Props) => {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
};

const ComboboxEmpty = ({ className, ...props }: ComboboxPrimitive.Empty.Props) => {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={mergeStyling(
        'text-gray-2 w-full justify-center pt-2 text-center text-sm group-data-empty/combobox-content:flex',
        className
      )}
      {...props}
    />
  );
};

const ComboboxSeparator = ({ className, ...props }: ComboboxPrimitive.Separator.Props) => {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={mergeStyling('bg-gray-4 -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
};

const ComboboxChips = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props) => {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={mergeStyling(
        'border-gray-4 focus-within:border-primary-2 has-aria-invalid:border-error flex min-h-8 flex-wrap items-center gap-1 border bg-transparent bg-clip-padding px-2.5 py-1 text-sm focus-within:ring-3 has-aria-invalid:ring-3 has-data-[slot=combobox-chip]:px-1',
        className
      )}
      {...props}
    />
  );
};

const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
}) => {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={mergeStyling(
        'bg-gray-5 text-gray-1 flex h-[1.3125rem] w-fit items-center justify-center gap-1 px-1.5 text-xs font-medium whitespace-nowrap has-data-[slot=combobox-chip-remove]:pr-0 has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50',
        className
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove className="-ml-1 opacity-50 hover:opacity-100" data-slot="combobox-chip-remove">
          <XIcon className="cursor-pointer text-gray-2 size-4" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
};

const ComboboxChipsInput = ({ className, ...props }: ComboboxPrimitive.Input.Props) => {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={mergeStyling('min-w-16 flex-1 outline-hidden', className)}
      {...props}
    />
  );
};

const useComboboxAnchor = () => {
  return React.useRef<HTMLDivElement | null>(null);
};

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
};
